import { AgGridReact } from 'ag-grid-react';
import { MDBIcon } from 'mdb-react-ui-kit';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './reports.css';
import { utils, writeFile } from 'xlsx';
import { splitDatesIntoIntervals } from '../../../service/utils';
import API from '../../../service/api';
import { CustomPopover } from '../../../components/popover/CustomPopover';

const NamesTooltip = (props) => {
    const data = useMemo(
        () => props.api.getDisplayedRowAtIndex(props.rowIndex).data,
        [],
    );

    if (props.node.rowPinned) return null;

    if (!data.prevNames) return null;

    return (
        <div
            className="custom-tooltip"
            style={{ backgroundColor: props.color || '#ececec' }}
        >
            <p>
                <Trans i18nKey="previous_names" />
            </p>
            {data.prevNames.map((n, idx) => (
                <p key={idx}>
                    <span>{n}</span>
                </p>
            ))}
        </div>
    );
};

const RowFromValue = (data) => {
    let row = {};
    row.name = data.names[0];
    if (data.names.length > 1) row.prevNames = data.names.slice(1);
    row.totalDrops = 0;
    row.idx = data.idx;
    data.drops.forEach((drops, idx) => {
        row[`i${idx}`] = { drops: drops[0] };
        row.totalDrops += drops[0];
    });

    return row;
};

const GetTotalsRow = (rows, productsCount, t) => {
    let totalsRow = {};
    totalsRow.name = `${t('total')} (${rows.length})`;
    totalsRow.totalDrops = 0;
    totalsRow.hidePercent = true;
    for (let i = 0; i < productsCount; i++) totalsRow[`i${i}`] = { drops: 0 };

    rows.forEach((r) => {
        totalsRow.totalDrops += r.totalDrops;
        for (let i = 0; i < productsCount; i++) {
            totalsRow[`i${i}`].drops += r[`i${i}`].drops;
        }
    });
    return totalsRow;
};

const ProductsColumns = (products) => {
    return products.map((src, idx) => {
        let name = src.name;

        return {
            field: `i${idx}`,
            headerName: name,
            width: 70,
            resizable: true,
            sortable: true,
            valueFormatter: ({ value }) => `${value.drops}`,
            comparator: (a, b) => a.drops - b.drops,
        };
    });
};

export const ProductsReportViewer = ({
    report,
    mobile,
    onNewReportRequest,
}) => {
    const rdata = useMemo(
        () =>
            Object.values(report.result.values).map((rr, idx) =>
                RowFromValue({ ...rr, idx }),
            ),
        [report],
    );
    const { t } = useTranslation();

    const [source] = useState(
        report.machine ? 'machine' : report.location ? 'location' : 'group',
    );
    const [totalsRow] = useState(
        GetTotalsRow(rdata, report.result.products.length, t),
    );

    const [gridApi, setGridApi] = useState(null);
    const [filter, setFilter] = useState({
        cash: true,
        card: true,
        total: true,
    });
    const [context, setContext] = useState(null);
    const grid = useRef();

    const [rowData] = useState(rdata);

    useEffect(() => {
        if (report && gridApi) {
            if (mobile) gridApi.setGridOption('columnDefs', []); //gridApi.setColumnDefs([]);

            gridApi.setGridOption(
                'columnDefs',
                [
                    {
                        field: 'elementname',
                        valueGetter: ({ data }) => data.name,
                        headerName: t('name'),
                        width: 250,
                        resizable: true,
                        sortable: true,
                        pinned: mobile ? undefined : 'left',
                        tooltipField: 'name',
                        tooltipComponent: NamesTooltip,
                        cellRenderer: ({ value, data, node }) => (
                            <span
                                onContextMenu={(ev) => {
                                    ev.preventDefault();
                                    ev.stopPropagation();
                                    onRowElementNameContext({
                                        dom: ev.target.parentElement
                                            .parentElement.parentElement,
                                    });
                                }}
                            >
                                {!node.rowPinned && (
                                    <span className="me-1 text-primary">
                                        <MDBIcon
                                            icon={
                                                source == 'group'
                                                    ? 'location-dot'
                                                    : 'server'
                                            }
                                        />
                                    </span>
                                )}
                                <span
                                    style={{
                                        fontWeight: node.rowPinned
                                            ? 'bold'
                                            : '',
                                    }}
                                >
                                    {value}
                                </span>
                                {data.prevNames && (
                                    <span className="ms-1 text-warning">
                                        <MDBIcon icon="asterisk" size="xs" />
                                    </span>
                                )}
                            </span>
                        ),
                    },
                    {
                        field: 'totalDrops',
                        headerName: t('total'),
                        width: 150,
                        resizable: true,
                        sortable: true,
                        pinned: mobile ? undefined : 'right',
                        cellRenderer: ({ value, node }) => {
                            const ratio =
                                value / (totalsRow.totalDrops / rowData.length);

                            let color = '';
                            if (ratio < 1) {
                                color = `rgba(255,0,0,${0.5 * (1 - ratio)})`;
                            } else {
                                color = `rgba(0,255,0,${Math.min(0.8, 0.5 * (ratio - 1))})`;
                            }

                            return (
                                <div
                                    className="d-flex flex-row justify-content-center h-100 w-100 align-items-center px-3"
                                    style={{
                                        background: node.rowPinned ? '' : color,
                                    }}
                                >
                                    <span className="d-flex flex-fill">
                                        {value}
                                    </span>
                                    {!node.rowPinned &&
                                        ratio !== null &&
                                        !value.hidePercent && (
                                            <span
                                                className="ms-2 text-muted"
                                                style={{ fontSize: '0.65rem' }}
                                            >
                                                {(ratio * 100).toFixed(1)}%
                                            </span>
                                        )}
                                </div>
                            );
                        },
                        // cellStyle: () => ({padding: 0}),
                        valueFormatter: ({ value }) => `${value}`,
                        comparator: (a, b) => a - b,
                    },
                ].concat(ProductsColumns(report.result.products)),
            );
        }

        if (gridApi) {
            const root = document.querySelector('.ag-root-wrapper');
            root.oncontextmenu = (ev) => {
                setContext(null);
                const header = ev.target.closest('.ag-header-cell');
                if (header) {
                    const id = header.getAttribute('col-id');
                    if (id[0] == 'i') {
                        onColumnHeaderContext({ dom: header });
                        ev.stopPropagation();
                        ev.preventDefault();
                    }
                }
            };
        }
    }, [gridApi, report, mobile, filter]);

    useEffect(() => {
        if (report && gridApi) {
            gridApi.setGridOption('rowData', rowData);
        }
    }, [gridApi, report, rowData]);

    const onColumnHeaderContext = ({ dom }) => {
        const id = dom.getAttribute('col-id'),
            rect = dom.getBoundingClientRect();

        const startDate = report.result.intervals[Number(id.slice(1))];
        const endDate =
            report.result.intervals[Number(id.slice(1)) + 1] || report.endDate;
        const gid = report.group ? report.group.id : undefined,
            lid = report.location ? report.location.id : undefined,
            mid = report.machine ? report.machine.id : undefined;

        setContext({
            x: rect.x + rect.width / 2,
            y: rect.y + rect.height / 2 + 10,
            position: 'bottom center',
            onReportRequest: () => {
                onReportRequest(startDate, endDate, gid, lid, mid);
            },
        });
    };

    const onRowElementNameContext = ({ dom }) => {
        const id = Number(dom.getAttribute('row-id')),
            rect = dom.getBoundingClientRect();
        const elId = Object.keys(report.result.values)[id];
        let gid = report.group ? report.group.id : undefined;
        let lid = report.location ? report.location.id : undefined;
        let mid = report.machine ? report.machine.id : undefined;

        setContext({
            x: rect.x + rect.width / 2,
            y: rect.y + rect.height / 2 + 10,
            position: 'right center',
            onReportRequest: () => {
                onReportRequest(
                    report.startDate,
                    report.endDate,
                    gid,
                    lid,
                    mid,
                    [Number(elId)],
                );
            },
        });
    };

    const onReportRequest = useCallback(
        (startDate, endDate, gid, lid, mid, prodList) => {
            let uri = '/api/groups';
            if (gid !== undefined) uri += `/${gid}`;
            if (lid !== undefined) uri += `/${lid}`;
            if (mid !== undefined) uri += `/${mid}`;
            uri += `/report`;

            const { resolution, intervals } = splitDatesIntoIntervals(
                startDate,
                endDate,
            );

            API.POST(uri, {
                startDate,
                endDate,
                resolution,
                intervals,
                prodList,
            }).then(({ id }) => {
                onNewReportRequest(id);
            });
        },
        [],
    );

    const exportToFile = (type) => {
        const sd = new Date(report.startDate);
        const ed = new Date(report.endDate);

        const sourceName = (
                report.machine
                    ? report.machine.name
                    : report.location
                      ? report.location.Name
                      : report.group.Name
            )
                .replace(/\s+/g, '_')
                .replace(/[^a-zA-Z0-9_\-\.]/g, ''),
            startDate = `${sd.getFullYear()}_${(sd.getMonth() + 1).toString().padStart(2, '0')}_${sd.getDate().toString().padStart(2, '0')}`,
            endDate = `${ed.getFullYear()}_${(ed.getMonth() + 1).toString().padStart(2, '0')}_${ed.getDate().toString().padStart(2, '0')}`;

        const filename = `${sourceName}_${report.type}_${startDate}-${endDate}`;
        const products = report.result.products.map((src) => src.name);

        let data = rowData.map((r) => {
            const row = [];
            row.push(r.name);

            products.forEach((_, idx) => {
                row.push(r[`i${idx}`].drops);
            });
            row.push(r.totalDrops);
            return row;
        });

        const ws = utils.aoa_to_sheet([
            [t('name'), ...products, t('total')],
            ...data,
        ]);
        const wb = utils.book_new();

        utils.book_append_sheet(wb, ws, 'Sheet1');

        if (type == 'excel') {
            writeFile(wb, `${filename}.xlsx`, { type: 'xlsx' });
        } else if (type == 'csv') {
            const csv = utils.sheet_to_csv(ws);
            const blob = new Blob([csv], { type: 'text/plain' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `${filename}.csv`;
            link.click();
            URL.revokeObjectURL(link.href);
        }
    };

    return (
        <div
            className="d-flex flex-column w-100 h-100 overflow-hidden"
            style={{ background: '#eeeeee' }}
        >
            <CustomPopover
                open={context}
                trigger={
                    <span
                        style={{
                            position: 'fixed',
                            left: context?.x ?? 0,
                            top: context?.y ?? 0,
                        }}
                    ></span>
                }
                placement={context?.position || 'bottom'}
                onClose={() => setContext(null)}
            >
                {(close) => (
                    <div className="d-flex flex-column">
                        <div
                            className="custom-select-item"
                            onClick={() => {
                                context.onReportRequest();
                                close();
                            }}
                        >
                            <MDBIcon
                                icon="calendar-days"
                                color="primary"
                                fixed
                                className="me-1"
                            />
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {t('new_report')}
                            </span>
                        </div>
                    </div>
                )}
            </CustomPopover>

            <div
                className={
                    'd-flex flex-column w-100 h-100 px-2 py-1 overflow-hidden ' +
                    (rowData.length >= (mobile ? -1 : 20) ? 'flex-fill' : '')
                }
            >
                <span className="d-flex flex-fill align-items-center justify-content-end">
                    <CustomPopover
                        placement="bottom-end"
                        trigger={
                            <button className="btn btn-link text-primary p-1">
                                <MDBIcon icon="download" className="me-2" />
                                <Trans i18nKey="export" />
                            </button>
                        }
                    >
                        {(close) => (
                            <div className="d-flex flex-column">
                                <div
                                    className="custom-select-item"
                                    onClick={() => {
                                        close();
                                        exportToFile('excel');
                                    }}
                                >
                                    <MDBIcon
                                        icon="file-excel"
                                        color="success"
                                        className="me-1"
                                    />
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {t('export_to_excel')}
                                    </span>
                                </div>
                                <div
                                    className="custom-select-item"
                                    onClick={() => {
                                        close();
                                        exportToFile('csv');
                                    }}
                                >
                                    <MDBIcon
                                        icon="file-csv"
                                        color="success"
                                        className="me-1"
                                    />
                                    <span
                                        style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {t('export_to_csv')}
                                    </span>
                                </div>
                            </div>
                        )}
                    </CustomPopover>
                </span>
                <div
                    className={
                        'ag-theme-alpine w-100 h-100 overflow-auto vertical-headers'
                    }
                    style={{}}
                >
                    <AgGridReact
                        ref={grid}
                        suppressMovableColumns
                        suppressColumnVirtualisation
                        suppressRowClickSelection
                        enableBrowserTooltips
                        scrollbarWidth={10}
                        tooltipShowDelay={0}
                        tooltipHideDelay={2000}
                        headerHeight={200}
                        pinnedBottomRowData={
                            totalsRow ? [totalsRow] : undefined
                        }
                        domLayout={undefined} //rowData.length >= (mobile ? -1 : 20) ? undefined : 'autoHeight'}
                        rowBuffer={20}
                        onGridReady={(event) => {
                            setGridApi(event.api);
                        }}
                    ></AgGridReact>
                </div>
            </div>
        </div>
    );
};
